// src/components/Dashboard_ADM/CentroNotificacoes.tsx
// UPDATED: 2026-03-27
// ✅ FIX: usa GET /notifications/all para admin ver TODAS as notificações do sistema
// ✅ Mantém stats, filtros, marcar lida, deletar, modais de envio

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Bell, Mail, MessageSquare, CheckCircle, Trash2, Send,
  Loader2, Search, RefreshCw, Eye, BellOff, Clock,
  Radio, AlertCircle,
} from 'lucide-react';
import apiClient from '../../services/api';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Notificacao {
  id: string;
  channel: 'email' | 'sms' | 'push' | 'in_app';
  status: 'pending' | 'sent' | 'delivered' | 'read' | 'failed';
  title: string;
  message: string;
  createdAt: string;
  readAt: string | null;
  userId?: string;
}

interface NotifStats {
  total: number; sent: number; delivered: number;
  read: number; failed: number; pending: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
const CHANNEL_CONFIG = {
  email:  { icon: Mail,          label: 'Email',  bg: 'bg-blue-100',   color: 'text-blue-700'   },
  sms:    { icon: MessageSquare, label: 'SMS',    bg: 'bg-green-100',  color: 'text-green-700'  },
  push:   { icon: Bell,          label: 'Push',   bg: 'bg-purple-100', color: 'text-purple-700' },
  in_app: { icon: Bell,          label: 'In-App', bg: 'bg-amber-100',  color: 'text-amber-700'  },
} as const;

const STATUS_CONFIG = {
  pending:   { label: 'Pendente', dot: 'bg-amber-400',  text: 'text-amber-700'  },
  sent:      { label: 'Enviado',  dot: 'bg-blue-400',   text: 'text-blue-700'   },
  delivered: { label: 'Entregue', dot: 'bg-indigo-400', text: 'text-indigo-700' },
  read:      { label: 'Lido',     dot: 'bg-green-400',  text: 'text-green-700'  },
  failed:    { label: 'Falhou',   dot: 'bg-red-500',    text: 'text-red-700'    },
} as const;

// ─── Componente ───────────────────────────────────────────────────────────────
const CentroNotificacoes: React.FC = () => {
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([]);
  const [stats, setStats]               = useState<NotifStats | null>(null);
  const [loading, setLoading]           = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [salvando, setSalvando]         = useState(false);
  const [abaAtiva, setAbaAtiva]         = useState<'todas' | 'nao_lidas'>('todas');

  const [busca, setBusca]               = useState('');
  const [filtroChannel, setFiltroChannel] = useState('');
  const [filtroStatus, setFiltroStatus]   = useState('');

  const [modalEmail, setModalEmail]         = useState(false);
  const [modalSMS, setModalSMS]             = useState(false);
  const [modalBroadcast, setModalBroadcast] = useState(false);
  const [emailForm, setEmailForm]           = useState({ to: '', subject: '', body: '' });
  const [smsForm, setSmsForm]               = useState({ to: '', message: '' });
  const [broadcastForm, setBroadcastForm]   = useState({ title: '', message: '', userType: 'all' });
  const [enviado, setEnviado]               = useState<string | null>(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const data = await apiClient.get<any>('/notifications/stats');
      setStats({
        total:     data.total     ?? 0,
        sent:      data.sent      ?? 0,
        delivered: data.delivered ?? 0,
        read:      data.read      ?? 0,
        failed:    data.failed    ?? 0,
        pending:   data.pending   ?? 0,
      });
    } catch { setStats(null); }
    finally { setLoadingStats(false); }
  }, []);

  const fetchNotificacoes = useCallback(async () => {
    setLoading(true);
    try {
      // ✅ /notifications/all → admin vê TODAS as notificações do sistema
      // /notifications/unread → só as não lidas do admin (usado na aba nao_lidas)
      const endpoint = abaAtiva === 'nao_lidas'
        ? '/notifications/unread'
        : '/notifications/all';

      const data = await apiClient.get<any>(endpoint);
      setNotificacoes(data.notifications ?? []);
    } catch { setNotificacoes([]); }
    finally { setLoading(false); }
  }, [abaAtiva]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchNotificacoes(); }, [fetchNotificacoes]);

  // ── Ações ──────────────────────────────────────────────────────────────────
  const handleMarcarLida = async (id: string) => {
    setSalvando(true);
    try {
      await apiClient.patch(`/notifications/${id}/read`, {});
      setNotificacoes(prev => prev.map(n =>
        n.id === id ? { ...n, status: 'read' as const, readAt: new Date().toISOString() } : n
      ));
    } catch { alert('Erro ao marcar como lida'); }
    finally { setSalvando(false); }
  };

  const handleMarcarTodasLidas = async () => {
    setSalvando(true);
    try {
      await apiClient.patch('/notifications/mark-all-read', {});
      fetchNotificacoes(); fetchStats();
    } catch { alert('Erro ao marcar todas como lidas'); }
    finally { setSalvando(false); }
  };

  const handleDeletar = async (id: string) => {
    if (!window.confirm('Deletar esta notificação?')) return;
    setSalvando(true);
    try {
      await apiClient.delete(`/notifications/${id}`);
      setNotificacoes(prev => prev.filter(n => n.id !== id));
      fetchStats();
    } catch { alert('Erro ao deletar'); }
    finally { setSalvando(false); }
  };

  const handleEnviarEmail = async () => {
    if (!emailForm.to || !emailForm.subject || !emailForm.body) { alert('Preencha todos os campos'); return; }
    setSalvando(true);
    try {
      await apiClient.post('/notifications/send-email', {
        email: emailForm.to, subject: emailForm.subject, body: emailForm.body,
        type: 'manual', plainText: emailForm.body,
      });
      setModalEmail(false); setEmailForm({ to: '', subject: '', body: '' });
      setEnviado('Email enviado com sucesso!');
      setTimeout(() => setEnviado(null), 4000);
      fetchNotificacoes();
    } catch { alert('Erro ao enviar email'); }
    finally { setSalvando(false); }
  };

  const handleEnviarSMS = async () => {
    if (!smsForm.to || !smsForm.message) { alert('Preencha todos os campos'); return; }
    setSalvando(true);
    try {
      await apiClient.post('/notifications/send-sms', {
        phone: smsForm.to, message: smsForm.message, type: 'manual',
      });
      setModalSMS(false); setSmsForm({ to: '', message: '' });
      setEnviado('SMS enviado com sucesso!');
      setTimeout(() => setEnviado(null), 4000);
      fetchNotificacoes();
    } catch { alert('Erro ao enviar SMS'); }
    finally { setSalvando(false); }
  };

  const handleBroadcast = async () => {
    if (!broadcastForm.title || !broadcastForm.message) { alert('Preencha título e mensagem'); return; }
    setSalvando(true);
    try {
      await apiClient.post('/admin/broadcast-notification', {
        title: broadcastForm.title, message: broadcastForm.message, userType: broadcastForm.userType,
      });
      setModalBroadcast(false); setBroadcastForm({ title: '', message: '', userType: 'all' });
      setEnviado('Broadcast enviado com sucesso!');
      setTimeout(() => setEnviado(null), 4000);
    } catch { alert('Erro ao enviar broadcast'); }
    finally { setSalvando(false); }
  };

  // ── Filtro local ───────────────────────────────────────────────────────────
  const notifFiltradas = useMemo(() => {
    return notificacoes.filter(n => {
      const mb = !busca || n.title?.toLowerCase().includes(busca.toLowerCase()) || n.message?.toLowerCase().includes(busca.toLowerCase());
      const mc = !filtroChannel || n.channel === filtroChannel;
      const ms = !filtroStatus  || n.status  === filtroStatus;
      return mb && mc && ms;
    });
  }, [notificacoes, busca, filtroChannel, filtroStatus]);

  const naoLidas = notificacoes.filter(n => n.status !== 'read' && n.status !== 'failed').length;

  return (
    <div className="space-y-6">

      {/* HEADER */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-1 flex items-center gap-3">
              Centro de Notificações
              {naoLidas > 0 && (
                <span className="px-2.5 py-0.5 rounded-full bg-red-500 text-white text-sm font-black">{naoLidas}</span>
              )}
            </h2>
            <p className="text-gray-600">Todas as notificações do sistema em tempo real</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button onClick={fetchNotificacoes}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">
              <RefreshCw className="w-5 h-5"/>
            </button>
            <button onClick={() => setModalEmail(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-blue-500 text-blue-600 font-bold hover:bg-blue-50">
              <Mail className="w-5 h-5"/> Email
            </button>
            <button onClick={() => setModalSMS(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-green-500 text-green-600 font-bold hover:bg-green-50">
              <MessageSquare className="w-5 h-5"/> SMS
            </button>
            <button onClick={() => setModalBroadcast(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold hover:shadow-lg">
              <Radio className="w-5 h-5"/> Broadcast
            </button>
          </div>
        </div>

        {/* Stats */}
        {loadingStats ? (
          <div className="flex justify-center py-4"><Loader2 className="w-8 h-8 text-blue-600 animate-spin"/></div>
        ) : stats ? (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            {[
              { label: 'Total',    value: stats.total,     bg: 'bg-gray-50',   border: 'border-gray-200'   },
              { label: 'Pendente', value: stats.pending,   bg: 'bg-amber-50',  border: 'border-amber-200'  },
              { label: 'Enviado',  value: stats.sent,      bg: 'bg-blue-50',   border: 'border-blue-200'   },
              { label: 'Entregue', value: stats.delivered, bg: 'bg-indigo-50', border: 'border-indigo-200' },
              { label: 'Lido',     value: stats.read,      bg: 'bg-green-50',  border: 'border-green-200'  },
              { label: 'Falhou',   value: stats.failed,    bg: 'bg-red-50',    border: 'border-red-200'    },
            ].map(s => (
              <div key={s.label} className={`${s.bg} rounded-xl p-4 border ${s.border}`}>
                <p className="text-xs text-gray-600 mb-1">{s.label}</p>
                <p className="text-2xl font-black text-gray-900">{s.value ?? 0}</p>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {enviado && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600"/>
          <p className="font-bold text-green-800">{enviado}</p>
        </div>
      )}

      {/* LISTA */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="p-5 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 bg-gray-100 rounded-xl p-1">
              {([
                { id: 'todas',     label: `Todas (${notificacoes.length})` },
                { id: 'nao_lidas', label: `Não lidas${naoLidas > 0 ? ` (${naoLidas})` : ''}` },
              ] as const).map(tab => (
                <button key={tab.id} onClick={() => setAbaAtiva(tab.id)}
                  className={`px-6 py-2 rounded-lg font-bold text-sm transition-all ${
                    abaAtiva === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
                  }`}>
                  {tab.label}
                </button>
              ))}
            </div>
            {naoLidas > 0 && (
              <button onClick={handleMarcarTodasLidas} disabled={salvando}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50 text-sm disabled:opacity-50">
                <CheckCircle className="w-4 h-4"/> Marcar todas como lidas
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="relative md:col-span-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"/>
              <input type="text" value={busca} onChange={e => setBusca(e.target.value)}
                placeholder="Buscar notificações…"
                className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 font-semibold"/>
            </div>
            <select value={filtroChannel} onChange={e => setFiltroChannel(e.target.value)}
              className="px-4 py-3 border-2 border-gray-300 rounded-xl text-gray-900 font-semibold">
              <option value="">Todos os Canais</option>
              {Object.entries(CHANNEL_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
              className="px-4 py-3 border-2 border-gray-300 rounded-xl text-gray-900 font-semibold">
              <option value="">Todos os Status</option>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="w-10 h-10 text-blue-600 animate-spin"/>
            <p className="text-gray-500 font-semibold">Carregando notificações…</p>
          </div>
        ) : notifFiltradas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <BellOff className="w-16 h-16 text-gray-300 mx-auto mb-4"/>
            <p className="text-gray-600 font-black text-lg">Nenhuma notificação encontrada</p>
            <p className="text-gray-400 text-sm mt-1">Ajuste os filtros ou aguarde novas atividades</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notifFiltradas.map(notif => {
              const ch = CHANNEL_CONFIG[notif.channel] ?? CHANNEL_CONFIG.in_app;
              const st = STATUS_CONFIG[notif.status]   ?? STATUS_CONFIG.pending;
              const ChIcon = ch.icon;
              const isUnread = notif.status !== 'read' && notif.status !== 'failed';
              return (
                <div key={notif.id} className={`p-5 hover:bg-gray-50 transition-colors ${isUnread ? 'bg-blue-50/20' : ''}`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${ch.bg}`}>
                      <ChIcon className={`w-5 h-5 ${ch.color}`}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <p className={`font-bold text-gray-900 ${isUnread ? 'font-black' : ''}`}>{notif.title}</p>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${ch.bg} ${ch.color}`}>{ch.label}</span>
                        <span className="flex items-center gap-1 text-xs">
                          <span className={`w-2 h-2 rounded-full ${st.dot}`}/>
                          <span className={`${st.text} font-semibold`}>{st.label}</span>
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{notif.message}</p>
                      <p className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock className="w-3 h-3"/>
                        {new Date(notif.createdAt).toLocaleString('pt-BR')}
                        {notif.readAt && ` · Lida: ${new Date(notif.readAt).toLocaleString('pt-BR')}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isUnread && (
                        <button onClick={() => handleMarcarLida(notif.id)} disabled={salvando}
                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg" title="Marcar como lida">
                          <Eye className="w-5 h-5"/>
                        </button>
                      )}
                      <button onClick={() => handleDeletar(notif.id)} disabled={salvando}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Deletar">
                        <Trash2 className="w-5 h-5"/>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* MODAL EMAIL */}
      {modalEmail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-black text-gray-900 flex items-center gap-2"><Mail className="w-5 h-5 text-blue-600"/> Enviar Email</h3>
              <button onClick={() => setModalEmail(false)} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
            </div>
            <div className="p-6 space-y-4">
              {[
                { label: 'Para *', key: 'to', type: 'email', ph: 'email@usuario.com' },
                { label: 'Assunto *', key: 'subject', type: 'text', ph: 'Assunto do email' },
              ].map(f => (
                <div key={f.key}>
                  <label className="block text-sm font-bold text-gray-700 mb-2">{f.label}</label>
                  <input type={f.type} value={(emailForm as any)[f.key]}
                    onChange={e => setEmailForm(p => ({ ...p, [f.key]: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 text-gray-900"
                    placeholder={f.ph}/>
                </div>
              ))}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Corpo *</label>
                <textarea value={emailForm.body} onChange={e => setEmailForm(p => ({ ...p, body: e.target.value }))} rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 resize-none text-gray-900"
                  placeholder="Conteúdo do email…"/>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button onClick={() => setModalEmail(false)} className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">Cancelar</button>
              <button onClick={handleEnviarEmail} disabled={salvando}
                className="flex-1 px-4 py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-60">
                {salvando ? <Loader2 className="w-5 h-5 animate-spin"/> : <Send className="w-5 h-5"/>} Enviar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL SMS */}
      {modalSMS && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-black text-gray-900 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-green-600"/> Enviar SMS</h3>
              <button onClick={() => setModalSMS(false)} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Telefone *</label>
                <input value={smsForm.to} onChange={e => setSmsForm(p => ({ ...p, to: e.target.value }))} type="tel"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 text-gray-900"
                  placeholder="+55 11 99999-9999"/>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Mensagem * <span className="text-gray-400 text-xs">({smsForm.message.length}/160)</span></label>
                <textarea value={smsForm.message} onChange={e => setSmsForm(p => ({ ...p, message: e.target.value.slice(0, 160) }))} rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 resize-none text-gray-900"
                  placeholder="Mensagem SMS (máx. 160 caracteres)…"/>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button onClick={() => setModalSMS(false)} className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">Cancelar</button>
              <button onClick={handleEnviarSMS} disabled={salvando}
                className="flex-1 px-4 py-3 rounded-xl bg-green-600 text-white font-bold hover:bg-green-700 flex items-center justify-center gap-2 disabled:opacity-60">
                {salvando ? <Loader2 className="w-5 h-5 animate-spin"/> : <Send className="w-5 h-5"/>} Enviar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL BROADCAST */}
      {modalBroadcast && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-xl font-black text-gray-900 flex items-center gap-2"><Radio className="w-5 h-5 text-purple-600"/> Broadcast Massivo</h3>
              <button onClick={() => setModalBroadcast(false)} className="text-gray-400 hover:text-gray-600 text-2xl">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800 font-semibold flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0"/> Será enviada para todos os usuários do grupo selecionado.
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Destinatários</label>
                <select value={broadcastForm.userType} onChange={e => setBroadcastForm(p => ({ ...p, userType: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-gray-900 font-semibold">
                  <option value="all">Todos os usuários</option>
                  <option value="affiliate">Filiados</option>
                  <option value="student">Alunos</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Título *</label>
                <input value={broadcastForm.title} onChange={e => setBroadcastForm(p => ({ ...p, title: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 text-gray-900"
                  placeholder="Título da notificação"/>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Mensagem *</label>
                <textarea value={broadcastForm.message} onChange={e => setBroadcastForm(p => ({ ...p, message: e.target.value }))} rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 resize-none text-gray-900"
                  placeholder="Conteúdo da notificação…"/>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button onClick={() => setModalBroadcast(false)} className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">Cancelar</button>
              <button onClick={handleBroadcast} disabled={salvando}
                className="flex-1 px-4 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-bold hover:shadow-lg flex items-center justify-center gap-2 disabled:opacity-60">
                {salvando ? <Loader2 className="w-5 h-5 animate-spin"/> : <Radio className="w-5 h-5"/>} Enviar Broadcast
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CentroNotificacoes;